import Box from '@mui/material/Box';
import Link from '@mui/material/Link';
import Stack from '@mui/material/Stack';
import Avatar from '@mui/material/Avatar';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';

import { RouterLink } from 'src/routes/components';

import { useTranslate } from 'src/locales';
import { isShadowEmail } from 'src/services/candidates/candidates.utils';

import Iconify from 'src/components/iconify';
import EntityAvatar from 'src/components/entity-avatar';

import { RecordHeaderModel } from './record-panel.types';

// ----------------------------------------------------------------------

/**
 * Shared record-panel header (JT1-1529). Renders the identity block — avatar, name, a status chip
 * (e.g. availability), a subtitle (job title / company) and a metadata row — plus contact details and
 * the single explicit "Open full profile" navigation. Each entity view feeds it a RecordHeaderModel,
 * so candidates/contacts/companies look identical. Empty meta items are already filtered by the view.
 */
export default function RecordPanelHeader({ model }: { model: RecordHeaderModel }) {
  const { t } = useTranslate();

  return (
    <Stack spacing={1.5} sx={{ px: 2.5, pt: 1, pb: 2 }}>
      <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={2}>
        <Stack direction="row" alignItems="center" spacing={2} sx={{ minWidth: 0 }}>
          <EntityAvatar
            src={model.avatarUrl}
            name={model.name}
            size={56}
            variant={model.avatar?.variant ?? 'circular'}
            editable={Boolean(model.avatar?.editable && model.avatar?.onChange)}
            outputFormat={model.avatar?.outputFormat}
            fallbackIcon={model.avatar?.fallbackIcon}
            cropTitle={model.avatar?.cropTitle}
            onChange={model.avatar?.onChange}
          />

          <Stack sx={{ minWidth: 0 }}>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ flexWrap: 'wrap' }}>
              <Typography variant="h5" noWrap sx={{ textTransform: 'capitalize' }}>
                {model.name}
              </Typography>
              {model.status}
            </Stack>
            {model.subtitle && (
              <Typography variant="body2" color="text.secondary" noWrap>
                {model.subtitle}
              </Typography>
            )}
          </Stack>
        </Stack>

        {model.fullProfileHref && (
          <Button
            component={RouterLink}
            href={model.fullProfileHref}
            size="small"
            variant="outlined"
            startIcon={<Iconify icon="solar:square-top-down-linear" />}
            sx={{ flexShrink: 0 }}
          >
            {t('common.open_full_profile', { defaultValue: 'Open full profile' })}
          </Button>
        )}
      </Stack>

      {model.meta.length > 0 && (
        <Stack direction="row" spacing={3} sx={{ flexWrap: 'wrap', rowGap: 1 }}>
          {model.meta.map((item) => (
            <Stack key={item.key} spacing={0.25} sx={{ minWidth: 0 }}>
              <Typography variant="caption" color="text.disabled">
                {item.label}
              </Typography>
              <Box sx={{ typography: 'body2' }}>{item.value}</Box>
            </Stack>
          ))}
        </Stack>
      )}

      {model.contact && (!isShadowEmail(model.contact.email) || model.contact.phone) && (
        <Stack direction="row" spacing={3} sx={{ flexWrap: 'wrap', rowGap: 0.5 }}>
          {!isShadowEmail(model.contact.email) && (
            <Link
              variant="body2"
              href={`mailto:${model.contact.email}`}
              color="inherit"
              sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}
            >
              <Iconify icon="mdi:email-outline" width={16} />
              {model.contact.email}
            </Link>
          )}
          {model.contact.phone && (
            <Link
              variant="body2"
              href={`tel:${model.contact.phone}`}
              color="inherit"
              sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}
            >
              <Iconify icon="solar:phone-linear" width={16} />
              {model.contact.phone}
            </Link>
          )}
        </Stack>
      )}
    </Stack>
  );
}
